/**
 * Smuggling scans (M5 spec §12.2, §14.2; plan C14).
 *
 * - **Patrol scan:** while tetherstone is illegal and in the player's hold, a patrol of the local jurisdiction
 *   within law.json `scan.rangeM` (2 km) of the player for `scan.seconds` (5 s) finds it: `progress` (0..1) drives
 *   C18's "Being scanned" meter; on completion the scan reports `smuggling` (the tonnes found) once and the cargo is
 *   confiscated. The hold time resets when no patrol is in range; a finished scan waits until no patrol is in range
 *   before it can start again.
 * - **Docking scans** run in the docking policy (`dockingPolicy.ts`).
 * - **Contraband story items (M7 S12, spec §5, SL-6):** a story item flagged `contraband` (Kell's package) makes the
 *   scan eligible in any jurisdiction, legal tetherstone or not. Its finding is a **fine, never confiscation**
 *   (`patrolScanFinding`; the fine and the wave-through are `src/story/m12.ts contrabandScan`).
 *
 * Allocation-free per tick.
 */
import lawJson from '../data/strategic/law.json'
import type { Sim } from '../sim'
import { Life, Pos64, Team } from '../world/components'
import { FACTION_TEAM } from './hostilityWriter'

export interface ScanTuning {
  rangeM: number
  seconds: number
}

export function defaultScanTuning(): ScanTuning {
  return { rangeM: lawJson.scan.rangeM, seconds: lawJson.scan.seconds }
}

export interface PatrolScanState {
  readonly tuning: ScanTuning
  /** Seconds a patrol has held within range. */
  holdS: number
  /** 0..1 for the HUD meter ("Being scanned"). */
  progress: number
  /** A patrol is scanning right now. */
  active: boolean
  /** The scanning patrol's entity (−1 = none). */
  patrol: number
  /** The scan finished and reported; cleared once no patrol is in range. */
  done: boolean
}

export function createPatrolScan(tuning: ScanTuning = defaultScanTuning()): PatrolScanState {
  return { tuning, holdS: 0, progress: 0, active: false, patrol: -1, done: false }
}

export function resetPatrolScan(s: PatrolScanState): void {
  s.holdS = 0
  s.progress = 0
  s.active = false
  s.patrol = -1
  s.done = false
}

/** Whether a live entity is one of the jurisdiction's patrols (the traffic layer knows roles). */
export type IsPatrol = (eid: number) => boolean

/** The nearest live patrol of `faction` within `rangeM` of the player, or −1. */
export function patrolInRange(sim: Sim, player: number, faction: number, rangeM: number, isPatrol: IsPatrol): number {
  const team = FACTION_TEAM[faction]
  const px = Pos64.x[player]
  const py = Pos64.y[player]
  const pz = Pos64.z[player]
  let best = -1
  let bestD2 = rangeM * rangeM
  const ships = sim.ships.dense
  for (let i = 0; i < ships.length; i++) {
    const e = ships[i]
    if (e === player || Life.alive[e] !== 1 || Team.id[e] !== team) continue
    const dx = Pos64.x[e] - px
    const dy = Pos64.y[e] - py
    const dz = Pos64.z[e] - pz
    const d2 = dx * dx + dy * dy + dz * dz
    if (d2 <= bestD2 && isPatrol(e)) {
      best = e
      bestD2 = d2
    }
  }
  return best
}

/**
 * One patrol-scan step of `dt` seconds. Returns true on the tick the scan completes (the caller reports
 * `smuggling` with the tonnes and confiscates). `jurisdiction` is the local holder's faction index (−1 lawless).
 */
export function stepPatrolScan(
  s: PatrolScanState,
  sim: Sim,
  dt: number,
  jurisdiction: number,
  illegal: boolean,
  tetherstoneT: number,
  isPatrol: IsPatrol,
  storyContraband = false,
): boolean {
  const player = sim.playerEid
  const cargo = (illegal && tetherstoneT > 0) || storyContraband
  const eligible = cargo && jurisdiction >= 0 && player >= 0 && Life.alive[player] === 1
  const patrol = eligible ? patrolInRange(sim, player, jurisdiction, s.tuning.rangeM, isPatrol) : -1
  s.patrol = patrol
  if (patrol < 0) {
    s.holdS = 0
    s.progress = 0
    s.active = false
    s.done = false
    return false
  }
  if (s.done) {
    s.active = false
    return false
  }
  s.active = true
  s.holdS += dt
  s.progress = s.holdS >= s.tuning.seconds ? 1 : s.holdS / s.tuning.seconds
  if (s.holdS < s.tuning.seconds) return false
  s.done = true
  s.active = false
  return true
}

/** What a finished patrol scan found (M7 S12): tetherstone is confiscated; a contraband story item is only fined. */
export interface PatrolScanFinding {
  /** Tonnes of illegal tetherstone to report as `smuggling` and confiscate (0 = none). */
  confiscateT: number
  /** A contraband story item is aboard: fine it (`contrabandScan`), never confiscate it. */
  storyFine: boolean
}

export function patrolScanFinding(illegal: boolean, tetherstoneT: number, storyContraband: boolean): PatrolScanFinding {
  return { confiscateT: illegal && tetherstoneT > 0 ? tetherstoneT : 0, storyFine: storyContraband }
}
